import type { IOnSubmitParams } from '@apps/Application/Application.types'
import type { SubmitAction } from '@apps/Application/widgets/AppSettingsForm/appSettingsForm.types'
import SlotSelector from '@apps/Application/widgets/SlotSelector'
import StopButton from '@apps/Application/widgets/StopButton'
import SubmitButton from '@apps/Application/widgets/SubmitButton'
import UninstallButton from '@apps/Application/widgets/UninstallButton'
import { emptyApplicationsArray } from '@common/constants'
import { F_VERSION_SELECTOR } from '@common/featureFlags/access'
import useAccessLevel from '@common/featureFlags/useAccessLevel'
import { useAppSelector, useMyApp, useTranslate } from '@common/hooks'
import getPermissions from '@common/utils/getPermissions'
import { getAppSlug } from '@common/utils/uuid'
import Button from '@common/widgets/Button'
import MenuButton from '@common/widgets/MenuButton'
import Header from '@layouts/Header'
import getSettings from '@utils/getSettings'
import { useRouter } from 'next/router'
import type { BaseSyntheticEvent } from 'react'

import classNames from './styles.module.scss'


interface IProps {
  slot: number,
  setSlot: (slot: number) => void,
  onSubmit: (params: IOnSubmitParams, confirmations: { slot: boolean, permissions: boolean }) => (e?: BaseSyntheticEvent| undefined) => Promise<any>,
  preventWindowClose: boolean,
  submitAction: SubmitAction,
  isSubmitting: boolean,
}

export default function AppHeader({ preventWindowClose, slot, setSlot, onSubmit, submitAction, isSubmitting }: IProps): JSX.Element {
  const { translate } = useTranslate()
  const router = useRouter()
  const previousListPage = useAppSelector(store => store.common.data.previousListPage)
  const application = useAppSelector(store => store.application?.data) as IApplication
  const userSlots = useAppSelector(store => store.userSlots.data?.items || emptyApplicationsArray)
  const versionSelectorAccess = useAccessLevel(F_VERSION_SELECTOR)
  const settings = getSettings(versionSelectorAccess && application.installed ? application.installed?.settings : application.latest?.settings)
  const permissions = getPermissions(application, userSlots) || []
  const cbUrl = useAppSelector(store => store.common.data.config?.CB_WEB_URL || '')
  const { isMyApp, isAppAuthor } = useMyApp(application)
  const slug = getAppSlug(application.id, application.name) as string
  return (
    <Header className={classNames.header}>
      <SlotSelector
        slot={slot}
        onClick={(slot: number) => setSlot(slot)}
        disabled={isMyApp}
        className={classNames.slotSelector}
      />
      <Button variant="light" onClick={() => previousListPage ? router.push(previousListPage) : router.push('/apps')} width="sm" className={classNames.backButton} data-test="back-btn" tabIndex={103}>
        <i className={classNames.backIcon}/>
        {translate('Back')}
      </Button>
      <SubmitButton
        application={application}
        settings={settings}
        permissions={permissions}
        className={classNames.actionBtn}
        onClick={onSubmit({ replaceSlot: false, submitAction, preventWindowClose }, { slot: true, permissions: true })}
        isLoading={isSubmitting}
        data-test="header-confirm-btn"
      />
      <MenuButton>
        {(isAppAuthor || application?.isPublic) && (
          <a
            href={`${cbUrl}/asp/devportal/?next=/ide/${application?.id}`}
            target="_blank"
            rel="noreferrer"
            data-test="app-details-source-link"
          >
            {translate('Source code')}
          </a>
        )}
        <a
          href={`${cbUrl}/app/${slug || ''}`}
          target="_blank"
          rel="noreferrer"
          data-test="app-details-current-users-link"
        >
          {translate('Current users')}
        </a>
        <StopButton />
        <UninstallButton />
      </MenuButton>
    </Header>
  )
}
